// Calendário de calor (DESIGN §5.17): célula de dia, grade do mês (aluno) e 90 dias (painel).
// Estado nunca só por cor: cada célula tem texto sr-only; legenda sempre visível.
import { cn } from "../lib/cn";
import { diasEntre, formatarDataCompleta, segundaDaSemana, somarDias } from "../lib/datas";

export type EstadoDia = "treino" | "checkin" | "vazio" | "futuro" | "fora" | "confirmando";

export interface DiaCalendario {
  data: string; // AAAA-MM-DD
  estado: EstadoDia;
}

const SR: Record<EstadoDia, string> = {
  treino: "treino registrado",
  checkin: "check-in sem treino",
  vazio: "sem registro",
  futuro: "",
  fora: "",
  confirmando: "confirmando check-in",
};

export interface PropsCelulaDia {
  dia: DiaCalendario;
  hoje?: boolean;
  /** Toque abre o detalhe (só em dias com registro). */
  aoClicar?: (data: string) => void;
  className?: string;
}

export function CelulaDia({ dia, hoje, aoClicar, className }: PropsCelulaDia) {
  const numero = Number(dia.data.slice(8, 10));
  const temRegistro = dia.estado === "treino" || dia.estado === "checkin";
  const classe = cn(
    "relative grid aspect-square place-items-center rounded-celula text-rotulo placar",
    dia.estado === "treino" && "corte bg-tinta text-sobre-tinta",
    dia.estado === "checkin" && "bg-tinta text-sobre-tinta-2",
    dia.estado === "vazio" && "bg-recuo text-texto-3",
    dia.estado === "futuro" && "border border-fio text-texto-3",
    dia.estado === "fora" && "opacity-0",
    dia.estado === "confirmando" && "border border-dashed border-info text-info",
    hoje && "ring-2 ring-inset ring-tinta",
    className,
  );
  const sr = [formatarDataCompleta(dia.data), SR[dia.estado], hoje ? "hoje" : ""].filter(Boolean).join(", ");
  const conteudo = (
    <>
      <span aria-hidden>{numero}</span>
      <span className="sr-only">{sr}</span>
      {hoje && <span aria-hidden className="absolute bottom-1 size-1.5 rounded-full bg-marca" />}
    </>
  );
  if (temRegistro && aoClicar) {
    return (
      <button type="button" data-dia={dia.data} className={classe} onClick={() => aoClicar(dia.data)}>
        {conteudo}
      </button>
    );
  }
  return (
    <div className={classe} aria-hidden={dia.estado === "fora" || undefined}>
      {conteudo}
    </div>
  );
}

const CABECALHO = ["S", "T", "Q", "Q", "S", "S", "D"];

/**
 * Grade do mês (semana começa na segunda). `dias` traz os dias do mês com estado;
 * o componente completa com células "fora" para alinhar as semanas.
 */
export function CalendarioMes({
  mes,
  dias,
  hoje,
  aoClicarDia,
  className,
}: {
  mes: string; // AAAA-MM
  dias: DiaCalendario[];
  hoje?: string;
  aoClicarDia?: (data: string) => void;
  className?: string;
}) {
  const primeiro = `${mes}-01`;
  const inicioGrade = segundaDaSemana(primeiro);
  const porData = new Map(dias.map((d) => [d.data, d]));
  const ultimo = dias.length ? dias[dias.length - 1]!.data : primeiro;
  const totalDias = diasEntre(ultimo, inicioGrade) + 1;
  const celulas = Math.ceil(totalDias / 7) * 7;

  return (
    <div className={className}>
      <div className="mb-1.5 grid grid-cols-7 gap-1.5" aria-hidden>
        {CABECALHO.map((c, i) => (
          <span key={i} className="text-center text-rotulo text-texto-3">
            {c}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5" role="group" aria-label={`Calendário de ${mes}`}>
        {Array.from({ length: celulas }, (_, i) => {
          const data = somarDias(inicioGrade, i);
          const dia = porData.get(data) ?? { data, estado: "fora" as const };
          return <CelulaDia key={data} dia={dia} hoje={data === hoje} aoClicar={aoClicarDia} />;
        })}
      </div>
    </div>
  );
}

/** Legenda obrigatória abaixo da grade. */
export function LegendaCalendario({ className }: { className?: string }) {
  const itens: { classe: string; texto: string }[] = [
    { classe: "corte bg-tinta", texto: "Treino" },
    { classe: "bg-tinta", texto: "Check-in" },
    { classe: "bg-recuo", texto: "Sem registro" },
  ];
  return (
    <ul className={cn("mt-3 flex flex-wrap gap-x-4 gap-y-1", className)}>
      {itens.map((i) => (
        <li key={i.texto} className="flex items-center gap-1.5 text-rotulo text-texto-2">
          <span aria-hidden className={cn("size-3 rounded-[3px]", i.classe)} />
          {i.texto}
        </li>
      ))}
    </ul>
  );
}

/**
 * Calor de 90 dias do painel: 13 colunas (semanas) × 7 linhas, células 12 px, sem número e sem corte.
 * `dias` em ordem crescente; tooltip nativo com data e estado; resumo em texto ao lado.
 */
export function CalorNoventaDias({
  dias,
  className,
}: {
  dias: { data: string; presenca: boolean; treino: boolean }[];
  className?: string;
}) {
  const presencas = dias.filter((d) => d.presenca).length;
  if (dias.length === 0) return null;
  const inicio = segundaDaSemana(dias[0]!.data);
  const porData = new Map(dias.map((d) => [d.data, d]));
  const colunas = Math.ceil((diasEntre(dias[dias.length - 1]!.data, inicio) + 1) / 7);

  return (
    <div className={cn("flex flex-wrap items-end gap-4", className)}>
      <div
        className="grid grid-flow-col grid-rows-7 gap-[3px]"
        style={{ gridTemplateColumns: `repeat(${colunas}, 12px)` }}
        aria-hidden
      >
        {Array.from({ length: colunas * 7 }, (_, i) => {
          const data = somarDias(inicio, i);
          const d = porData.get(data);
          const estado = !d ? "fora" : d.treino ? "Treino" : d.presenca ? "Check-in" : "Sem registro";
          return (
            <span
              key={data}
              title={d ? `${formatarDataCompleta(data)}: ${estado}` : undefined}
              className={cn(
                "size-3 rounded-[3px]",
                !d && "opacity-0",
                d && (d.presenca || d.treino) ? "bg-tinta" : d && "bg-recuo",
              )}
            />
          );
        })}
      </div>
      <p className="text-apoio text-texto-2">
        <span className="font-semibold text-tinta">{presencas} presenças</span> em {dias.length} dias
      </p>
    </div>
  );
}
